/**
 * Frame-suitability range for segment height when a lens height is known:
 * `15 <= SH <= vbox - 10`.
 *
 * This is a *fitting* rule, not a measurement-plausibility one — it says the
 * pupil needs room above it for distance vision and below it for the add. A
 * measurement outside it is usually correct, and the frame is the thing that
 * does not work. So it surfaces as a review warning and never rejects a
 * capture: rejecting would strand the user in a scan that cannot succeed,
 * with nothing said about the actual problem.
 *
 * The same expression is the order form's non-SV validation (`getShRange` in
 * the CA app), which additionally accounts for the frame's `extend_y`. This
 * check has no shape data, so it is the unextended form.
 */

export const SH_VBOX_RANGE = {
  /** Lowest segment height a varifocal corridor is workable at. */
  minMm: 15,
  /** Clearance required between the pupil and the top of the lens. */
  topOffsetMm: 10,
} as const

export type ShVboxRangeConfig = {
  minMm: number
  topOffsetMm: number
}

export type ShVboxRangeCheck = {
  minMm: number
  maxMm: number
  isLeftOutside: boolean
  isRightOutside: boolean
}

/**
 * `null` when the rule cannot be applied: no lens height, or a frame shallow
 * enough that the window inverts (below ~25 mm, where `vbox - 10 <= 15`).
 * Reporting nothing beats warning on every measurement of a frame the rule
 * has no opinion about.
 */
export const shVboxRange = (
  vboxMm: number,
  config: ShVboxRangeConfig = SH_VBOX_RANGE
): { minMm: number; maxMm: number } | null => {
  if (!Number.isFinite(vboxMm) || vboxMm <= 0) return null

  const maxMm = vboxMm - config.topOffsetMm
  if (maxMm <= config.minMm) return null

  return { minMm: config.minMm, maxMm }
}

/** `null` when the rule does not apply, or when both eyes are inside it. */
export const checkShVboxRange = (
  vboxMm: number,
  result: { left: number; right: number },
  config: ShVboxRangeConfig = SH_VBOX_RANGE
): ShVboxRangeCheck | null => {
  const range = shVboxRange(vboxMm, config)
  if (!range) return null

  const isOutside = (value: number) =>
    Number.isFinite(value) && (value < range.minMm || value > range.maxMm)

  const isLeftOutside = isOutside(result.left)
  const isRightOutside = isOutside(result.right)
  if (!isLeftOutside && !isRightOutside) return null

  return { ...range, isLeftOutside, isRightOutside }
}

/**
 * Trims a trailing `.0` so whole millimetres read as `32`, not `32.0`. Only for
 * the package's own English fallback — a localized message should format the
 * numbers itself, since the decimal separator is a comma in most of our
 * locales.
 */
export const formatShVboxRangeMm = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(1)
